// ExtraChargeModal — rebuilt as the two-step "Add Estimate" flow (P4.3).
// The previous version instantly created an invoice when the tech tapped
// Save — wrong default, no customer approval. Now:
//   Step 1: enter line items + total + optional note.
//   Step 2: review → "Send to customer" emails/SMSes the /estimate/:token link.
// Approved estimates auto-create an invoice via the estimate-respond Edge Function.

import { useState } from 'react';
import { useApp } from '@/context/AppContext';

const EMPTY_ROW = () => ({ description: '', qty: 1, unit_price: 0 });

export default function ExtraChargeModal({ data }) {
  const { custById, stops, createEstimate, sendEstimate, closeModal } = useApp();

  const stopId = data?.stopId ?? null;
  const stop = stopId ? stops.find(s => s.id === stopId) : null;
  const custId = data?.custId ?? stop?.custId ?? null;
  const cust = custId ? custById(custId) : null;

  const [rows,  setRows]  = useState([EMPTY_ROW()]);
  const [notes, setNotes] = useState('');
  const [step,  setStep]  = useState('compose'); // 'compose' | 'sending' | 'sent'
  const [error, setError] = useState(null);
  const [createdEstimate, setCreatedEstimate] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  const total = rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);

  function updateRow(i, patch) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }

  async function handleSendNow() {
    setError(null);
    const cleaned = rows
      .map(r => ({
        description: (r.description || '').trim(),
        qty:         Number(r.qty) || 0,
        unit_price:  Number(r.unit_price) || 0,
      }))
      .filter(r => r.description && r.qty > 0);
    if (cleaned.length === 0) {
      setError('Add at least one line item with a description and quantity.');
      return;
    }
    if (!custId) {
      setError('No customer selected.');
      return;
    }

    setStep('sending');

    const created = await createEstimate({
      customerId: custId,
      stopId,
      lineItems: cleaned,
      notes: notes.trim() || null,
    });
    if (!created.ok) {
      setStep('compose');
      setError(created.error?.message || 'Could not save estimate');
      return;
    }
    setCreatedEstimate(created.estimate);

    const sent = await sendEstimate(created.estimate.id);
    setSendResult(sent);
    setStep('sent');
  }

  return (
    <div className="modal">
      <div className="modal-head">
        <span className="modal-title">
          {step === 'sent' ? 'Estimate sent' : 'New Estimate'}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
      </div>
      <div className="modal-body">
        {cust && (
          <div style={{
            marginBottom: 14, padding: '10px 12px', background: 'var(--s2)',
            borderRadius: 8, fontSize: 13, color: 'var(--t2)',
          }}>
            For <strong style={{ color: 'var(--t1)' }}>{cust.name}</strong> · {cust.addr}
            {!cust.email && !cust.phone && (
              <div style={{ color: 'var(--rose)', marginTop: 4, fontSize: 12 }}>
                ⚠ This customer has no email or phone — add one before sending.
              </div>
            )}
          </div>
        )}

        {step === 'compose' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
              Line items
            </div>
            {rows.map((r, i) => (
              <div key={i} className="form-row" style={{ alignItems: 'flex-end', marginBottom: 8 }}>
                <div className="field" style={{ flex: 3 }}>
                  <label>Description</label>
                  <input
                    value={r.description}
                    onChange={e => updateRow(i, { description: e.target.value })}
                    placeholder="Filter clean, repair, etc."
                  />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>Qty</label>
                  <input
                    type="number" min="0" step="1" inputMode="numeric"
                    value={r.qty}
                    onChange={e => updateRow(i, { qty: e.target.value })}
                  />
                </div>
                <div className="field" style={{ flex: 1.5 }}>
                  <label>Unit ($)</label>
                  <input
                    type="number" min="0" step="0.01" inputMode="decimal"
                    value={r.unit_price}
                    onChange={e => updateRow(i, { unit_price: e.target.value })}
                  />
                </div>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ marginBottom: 12 }}
                    onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))}
                  >✕</button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginBottom: 14 }}
              onClick={() => setRows(rs => [...rs, EMPTY_ROW()])}
            >
              + Add line item
            </button>

            <div className="field">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Anything the customer should know before approving…"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--b2)' }}>
              <span style={{ fontSize: 13, color: 'var(--t3)' }}>Total</span>
              <span style={{ fontSize: 22, fontWeight: 700 }}>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>

            {error && (
              <div style={{ marginTop: 10, padding: 10, background: 'var(--rose-t)', border: '1px solid var(--rose)', borderRadius: 8, fontSize: 13 }}>
                {error}
              </div>
            )}

            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendNow}
                disabled={total <= 0}
              >
                Send to customer
              </button>
            </div>
          </>
        )}

        {step === 'sending' && (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--t3)' }}>Sending…</div>
        )}

        {step === 'sent' && createdEstimate && (
          <div>
            <div style={{
              padding: 14, background: 'var(--teal-t)', border: '1px solid var(--teal)',
              borderRadius: 9, fontSize: 14, marginBottom: 14,
            }}>
              Estimate <strong>#{createdEstimate.id}</strong> for <strong>${createdEstimate.total.toLocaleString()}</strong> has been
              {sendResult?.anySent ? ' sent to the customer' : ' created'}.
            </div>
            {sendResult?.link && (
              <div style={{ fontSize: 12, color: 'var(--t2)' }}>
                Share link:
                <code style={{ display: 'block', marginTop: 4, padding: 8, background: 'var(--s2)', borderRadius: 6, wordBreak: 'break-all' }}>
                  {sendResult.link}
                </code>
              </div>
            )}
            {sendResult && !sendResult.anySent && (
              <div style={{ fontSize: 12, color: 'var(--rose)', marginTop: 8 }}>
                No email/SMS provider was reachable — the link above can be shared manually until Resend/Twilio are configured.
              </div>
            )}
            <div className="modal-foot">
              <button type="button" className="btn btn-primary" onClick={closeModal}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
